import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Sparkles, Send, Lock, MessageSquare, Flame, CheckCheck, Bot, UserRound, EyeOff } from 'lucide-react';
import { formatMessageHTML } from '../../utils';
import { MessageAttachment } from './MessageAttachment';
import { ChatMessage, OutgoingMessage, OutgoingMessageKind, isInternalNote } from './messageTypes';
import '../../styles/chats.css';

interface ChatMessagePanelProps {
  activeChat: any;
  messages: ChatMessage[];
  isTyping: boolean;
  input: string;
  setInput: (v: string) => void;
  /**
   * Takes the whole payload, so the composer has to state what it is sending.
   * It used to be `onSend: () => void` — no arguments at all — which is why the
   * note toggle changed nothing but the colours.
   */
  onSend: (payload: OutgoingMessage) => void;
  onAiRespond: () => void;
  onRetryMessage: (messageId: number) => void;
  hasOlderMessages: boolean;
  onLoadOlderMessages: () => void;
  onToggleAi: (id: number, enabled: boolean) => void;
  onBackToChats: () => void;
  msgEndRef: React.RefObject<HTMLDivElement | null>;
  channels: Record<string, any>;
  avatarColor: (name: string) => string;
  initials: (name: string) => string;
}

const QUICK_TEMPLATES = [
  'Здравствуйте! Чем могу помочь?',
  'Отправил Вам коммерческое предложение',
  'Спасибо за обращение! Ожидайте звонка менеджера',
  'Подскажите удобное время для связи?',
];

const formatTime = (dateStr: unknown): string => {
  if (!dateStr) return '';
  const d = new Date(String(dateStr));
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const AUTHOR_LABELS: Record<string, string> = {
  manager: 'Менеджер',
  ai: 'ИИ-агент',
  client: 'Клиент',
};

export const ChatMessagePanel: React.FC<ChatMessagePanelProps> = ({
  activeChat,
  messages,
  isTyping,
  input,
  setInput,
  onSend,
  onAiRespond,
  onRetryMessage,
  hasOlderMessages,
  onLoadOlderMessages,
  onToggleAi,
  onBackToChats,
  msgEndRef,
  channels,
  avatarColor,
  initials,
}) => {
  const [mode, setMode] = useState<OutgoingMessageKind>('reply');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const ch = channels[activeChat.channelType || activeChat.channel?.type] || channels.widget;
  const isNoteMode = mode === 'note';

  // Auto-adjust textarea height up to 120px
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  /**
   * The single place a message leaves this component. The button and the Enter
   * key both go through it, so the two can never disagree about the mode.
   */
  const submit = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    onSend(mode === 'note' ? { kind: 'note', text } : { kind: 'reply', text });
  }, [input, mode, onSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }, [submit]);

  const selectReplyMode = useCallback(() => setMode('reply'), []);
  const selectNoteMode = useCallback(() => setMode('note'), []);
  const handleSelectTemplate = useCallback((text: string) => {
    setInput(text);
    textareaRef.current?.focus();
  }, [setInput]);
  const handleToggleAi = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onToggleAi(activeChat.id, e.target.checked),
    [activeChat.id, onToggleAi],
  );

  const renderedMessages = useMemo(() => (Array.isArray(messages) ? messages : []), [messages]);

  return (
    <div className="chat-main">
      {/* Enhanced Header */}
      <div className="chat-header">
        <button className="icon-btn back-btn" onClick={onBackToChats}>
          <ArrowLeft size={20} />
        </button>
        <div className="avatar" style={{ '--av': avatarColor(activeChat.contactName) } as any}>
          {initials(activeChat.contactName)}
        </div>
        <div className="ch-info">
          <div className="ch-name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{activeChat.contactName}</span>
            <span
              className="chbadge"
              style={{ width: 16, height: 16, background: ch.color }}
              dangerouslySetInnerHTML={{ __html: ch.svg }}
            />
            {activeChat.temp === 'hot' && (
              <span className="chat-hot-pill">
                <Flame size={10} /> Горячий
              </span>
            )}
          </div>
          <div className="ch-status" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{ch.name}</span>
            <span style={{ opacity: 0.4 }}>•</span>
            <span style={{ color: activeChat.aiEnabled ? '#8b5cf6' : '#64748b', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              {activeChat.aiEnabled ? <><Bot size={13} aria-hidden="true" /> ИИ-ассистент активен</> : <><UserRound size={13} aria-hidden="true" /> Отвечает менеджер</>}
            </span>
          </div>
        </div>

        <div className="ch-actions">
          <label className="ai-toggle" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: activeChat.aiEnabled ? '#7c3aed' : '#64748b' }}>
              ✦ ИИ в чате
            </span>
            <span className="switch">
              <input
                type="checkbox"
                checked={!!activeChat.aiEnabled}
                onChange={handleToggleAi}
              />
              <span className="slider"></span>
            </span>
          </label>
        </div>
      </div>

      {/* Messages Feed */}
      <div className="chat-messages">
        {hasOlderMessages && (
          <button type="button" className="message-load-older" onClick={onLoadOlderMessages}>
            Загрузить предыдущие сообщения
          </button>
        )}
        {renderedMessages.map((m, i) => {
          if (!m || typeof m !== 'object') return null;
          // A note is a note because the server says so — never because of a
          // local flag the composer happened to set.
          const isNote = isInternalNote(m);
          const authorLabel = AUTHOR_LABELS[m.author] || 'Сотрудник';

          if (isNote) {
            return (
              <div key={m.id || i} className="msg note-row">
                <div className="note-card">
                  <div className="note-head">
                    <Lock size={11} aria-hidden="true" />
                    <span className="note-title">Внутренняя заметка команды</span>
                    <span className="note-meta">{authorLabel} · {formatTime(m.sentAt)}</span>
                  </div>
                  <div className="note-body" dangerouslySetInnerHTML={{ __html: formatMessageHTML(m.text || '') }} />
                  {m.mediaUrl && <MessageAttachment message={m} />}
                  <div className="note-foot">
                    <EyeOff size={11} aria-hidden="true" /> Клиент этого не видит — сообщение не отправлялось в канал
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={m.id || i} className={`msg ${m.direction === 'in' ? 'in' : 'out'}`}>
              <div className={`bubble ${m.author === 'ai' ? 'ai' : ''}`}>
                {m.author === 'ai' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                    <span className="ai-mark">✦ ИИ-АГЕНТ</span>
                  </div>
                )}
                {m.mediaUrl && <MessageAttachment message={m} />}
                <span dangerouslySetInnerHTML={{ __html: formatMessageHTML(m.text || '') }} />
                <span className="m-time" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                  {formatTime(m.sentAt)}
                  {m.direction === 'out' && m.deliveryStatus === 'delivered' && (
                    <CheckCheck size={12} style={{ color: '#6366f1', marginLeft: '3px' }} />
                  )}
                  {m.direction === 'out' && ['queued', 'sending'].includes(String(m.deliveryStatus)) && (
                    <span
                      aria-label="Сообщение ожидает отправки"
                      title={m.deliveryError ? `Ожидает повторной отправки: ${m.deliveryError}` : 'Сообщение отправляется'}
                    >…</span>
                  )}
                  {m.direction === 'out' && m.deliveryStatus === 'failed' && (
                    <button type="button" className="message-retry" onClick={() => onRetryMessage(m.id)} title={m.deliveryError || 'Повторить отправку'}>
                      Повторить
                    </button>
                  )}
                </span>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="msg out typing">
            <div className="bubble ai">
              <i></i>
              <i></i>
              <i></i>
            </div>
          </div>
        )}
        <div ref={msgEndRef} />
      </div>

      {/* AI Assistant Suggestions Bar */}
      <div className="ai-suggestions">
        <span className="ai-suggestions-label">
          <Sparkles size={13} /> Быстрые действия:
        </span>
        <button className="sugg-chip sugg-chip-ai" onClick={onAiRespond}>
          ✦ Сгенерировать ответ ИИ
        </button>
        {QUICK_TEMPLATES.map((tpl) => (
          <button
            key={tpl}
            className="sugg-chip sugg-chip-template"
            onClick={() => handleSelectTemplate(tpl)}
          >
            {tpl}
          </button>
        ))}
      </div>

      {/* Composer */}
      <div className="chat-input-wrapper">
        <div className="composer-modes" role="group" aria-label="Режим отправки">
          <button
            type="button"
            className={`composer-mode ${!isNoteMode ? 'active' : ''}`}
            aria-pressed={!isNoteMode}
            onClick={selectReplyMode}
          >
            <MessageSquare size={13} /> Ответ клиенту
          </button>
          <button
            type="button"
            className={`composer-mode note ${isNoteMode ? 'active' : ''}`}
            aria-pressed={isNoteMode}
            onClick={selectNoteMode}
          >
            <Lock size={13} /> Внутренняя заметка
          </button>
        </div>

        {isNoteMode && (
          <p className="composer-note-hint" role="status">
            <EyeOff size={12} aria-hidden="true" /> Заметка сохранится в диалоге для коллег и не будет отправлена клиенту.
          </p>
        )}

        <div className={`chat-input ${isNoteMode ? 'note-mode' : ''}`}>
          <div className="field">
            <textarea
              ref={textareaRef}
              placeholder={isNoteMode
                ? 'Напишите внутреннюю заметку для коллег — клиент её не увидит...'
                : 'Напишите ответ клиенту... (Enter — отправить, Shift+Enter — новая строка)'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              aria-label={isNoteMode ? 'Текст внутренней заметки' : 'Текст ответа клиенту'}
            />
            <button
              type="button"
              className="ai-gen-btn"
              title="Сгенерировать ответ ИИ"
              onClick={onAiRespond}
            >
              <Sparkles size={18} />
            </button>
          </div>
          <button
            type="button"
            className={`send-btn ${isNoteMode ? 'note' : ''}`}
            onClick={submit}
            disabled={!input.trim()}
            title={isNoteMode ? 'Сохранить внутреннюю заметку' : 'Отправить ответ клиенту'}
            aria-label={isNoteMode ? 'Сохранить внутреннюю заметку' : 'Отправить ответ клиенту'}
          >
            {isNoteMode ? <Lock size={18} /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};
